import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { Box, Typography, Paper, List, ListItem, ListItemText, Divider } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentSuccessSkeleton from '../components/skeletons/PaymentSuccessSkeleton';
import { trackPageView, trackPurchase, trackWithUserContext, getRestaurantId } from '../utils/analytics';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

interface PaymentDetails {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: string;
  orderId: string;
  order: {
    id: string;
    total: number;
    currency: string;
  };
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    paidAmount: number;
    notes?: string;
    options?: Record<string, any>;
    user?: {
      id: string;
      name: string;
    };
    status: string;
  }>;
}

const PaymentSuccess: React.FC = () => {
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get('paymentId');
  const orderId = searchParams.get('orderId'); // Fallback for legacy URLs
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const tableId = localStorage.getItem('tableId');

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        if (paymentId) {
          // Fetch payment details using paymentId
          const response = await api.get(`/payments/${paymentId}/details`);
          setPaymentDetails(response.data);
          
          // Extract restaurant ID
          const restaurantId = getRestaurantId(response.data.order);
          
          // Track successful payment completion with enhanced context
          trackPageView(window.location.href, `Payment Success - ${paymentId}`, restaurantId);
          
          // Calculate social payment insights
          const myItems = response.data.items?.filter((item: any) => item.user?.id === currentUser?.id) || [];
          const othersItems = response.data.items?.filter((item: any) => item.user?.id !== currentUser?.id) || [];
          const selfAmount = myItems.reduce((sum: number, item: any) => sum + item.paidAmount, 0);
          const othersAmount = othersItems.reduce((sum: number, item: any) => sum + item.paidAmount, 0);
          
          trackWithUserContext(
            'payment_completed',
            'conversion',
            response.data.id,
            response.data.amount,
            {
              provider: response.data.provider,
              currency: response.data.currency,
              order_id: response.data.orderId,
              is_social_payment: othersAmount > 0,
              self_amount: selfAmount,
              others_amount: othersAmount,
              paying_for_count: othersItems.length > 0 ? new Set(othersItems.map((item: any) => item.user?.id)).size : 0
            },
            currentUser,
            tableId || undefined,
            restaurantId
          );
          
          // Track purchase event for e-commerce analytics
          const purchaseItems = response.data.items.map((item: any) => ({
            item_id: item.id,
            item_name: item.name,
            quantity: item.quantity,
            price: item.price,
          }));
          trackPurchase(response.data.id, response.data.amount, response.data.currency, purchaseItems);
          
        } else if (orderId) {
          // Fallback: fetch order details (legacy behavior)
          const response = await api.get(`/orders/${orderId}`);
          const orderData = response.data;
          
          // Convert order data to payment details format for backward compatibility
          const fallbackPaymentDetails: PaymentDetails = {
            id: 'legacy',
            amount: orderData.total,
            currency: orderData.currency || 'USD',
            status: 'succeeded',
            provider: 'unknown',
            createdAt: new Date().toISOString(),
            orderId: orderData.id,
            order: {
              id: orderData.id,
              total: orderData.total,
              currency: orderData.currency || 'USD'
            },
            items: orderData.items || []
          };
          setPaymentDetails(fallbackPaymentDetails);
          
          // Extract restaurant ID for legacy flow
          const legacyRestaurantId = getRestaurantId(orderData);
          
          // Track legacy payment success with enhanced context
          trackPageView(window.location.href, `Payment Success - Order ${orderId}`, legacyRestaurantId);
          trackWithUserContext(
            'payment_completed_legacy',
            'conversion',
            orderId || 'unknown',
            orderData.total,
            {
              provider: 'unknown',
              currency: orderData.currency || 'USD',
              order_id: orderId,
              legacy_flow: true
            },
            currentUser,
            tableId || undefined,
            legacyRestaurantId
          );
          
        } else {
          throw new Error('No payment ID or order ID provided');
        }
      } catch (error) {
        console.error('Error fetching payment details:', error);
        setError('No se pudieron obtener los detalles del pago');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [paymentId, orderId]);

  if (loading) return <PaymentSuccessSkeleton />;
  if (error) return <Box sx={{ mt: 4 }}><Typography color="error">{error}</Typography></Box>;
  if (!paymentDetails) return <Box sx={{ mt: 4 }}><Typography>Detalles del pago no encontrados</Typography></Box>;

  // Calculate totals
  const paidAmount = paymentDetails.amount;
  const items = paymentDetails.items || [];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      width: '100vw', 
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)', 
      py: 4 
    }}>
      <Box maxWidth="sm" sx={{ mx: 'auto', pb: { xs: 10, sm: 4 } }}>
        <Box sx={{ 
          mt: 0, 
          mb: 3,
          mr: 3,
          ml: 3,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <CheckCircleOutlineIcon sx={{ 
            fontSize: 80, 
            color: '#4caf50',
            animation: 'scaleIn 0.5s ease-out'
          }} />
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#2e7d32' }}>
            ¡Pago Exitoso!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Gracias por tu pago. Aquí están los artículos que pagaste:
          </Typography>
        </Box>
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          mr: 3,
          ml: 3,
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          background: 'white',
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 600 }}>
            Resumen del Pago
          </Typography>
          <List>
            {items.map((item: any) => (
              <ListItem key={item.id} sx={{ px: 0 }}>
                <ListItemText
                  primary={<Typography fontWeight={600} component="span">{item.name}</Typography>}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Cantidad: {item.quantity}
                      </Typography>                      
                    </Box>
                  }
                />
                <Box sx={{ textAlign: 'right' }}>
                                          <Typography fontWeight={600}>₡{((typeof item.price === 'number' ? item.price : Number(item.price)) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>                  
                </Box>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 3,
            pt: 2,
            borderTop: '2px solid #e0e0e0'
          }}>
            <Typography variant="h6" sx={{ color: '#2e7d32' }}>Monto Pagado:</Typography>
                                <Typography variant="h6" sx={{ color: '#2e7d32' }}>₡{Number(paidAmount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          </Box>
        </Paper>
      </Box>
      <style>
        {`
          @keyframes scaleIn {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

export default PaymentSuccess; 